import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const useFinalizationDataStore = create(
  devtools((set, get) => ({
    // Applied tools data for finalization display
    appliedTools: {
      rectification: {
        measureReduction: {
          applied: false,
          measurements: [], // { id, scan { circumference, medioLat, antPost }, rectified { circumference, medioLat, antPost }, percentage, falloff, height }
          appliedAt: null
        },
        globalReduction: {
          applied: false,
          value: 0,
          valueType: '%',
          unit: 'mm',
          appliedAt: null
        },
        scoopRecharge: {
          applied: false,
          zones: [], // { id, name, summitValue, color, points, bezierHandles, influences }
          appliedAt: null
        },
        smoothening: {
          applied: false,
          zones: [], // { id, name, smootheningValue, color, isExpanded, influences }
          appliedAt: null
        }
      },
      flares: {
        flare: {
          applied: false,
          flareValues: [],
          segmentHeightValues: [],
          segmentDeflectValues: [],
          segmentFalloffHeightValues: [],
          segmentFalloffDeflectValues: [],
          deflectBlendRatio: 0.75,
          appliedAt: null,
        },
        smoothening: {
          applied: false,
          zones: [],
          appliedAt: null,
        },
      },
      elongation: {
        applied: false,
        value: 0.0,
        unit: 'mm',
        appliedAt: null
      },
      thickness: {
        applied: false,
        value: 0.0,
        topValue: 0.0, // For mould socket type
        unit: 'mm',
        appliedAt: null
      },
      trimline: {
        applied: false,
        appliedAt: null
      }
    },

    // Actions to save applied tool data
    saveRectificationMeasureReduction: (measurements) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          rectification: {
            ...state.appliedTools.rectification,
            measureReduction: {
              applied: true,
              measurements: JSON.parse(JSON.stringify(measurements)), // Deep clone
              appliedAt: new Date().toISOString()
            }
          }
        }
      }));
    },

    saveRectificationGlobalReduction: (value, valueType, unit) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          rectification: {
            ...state.appliedTools.rectification,
            globalReduction: {
              applied: true,
              value,
              valueType,
              unit,
              appliedAt: new Date().toISOString()
            }
          }
        }
      }));
    },

    saveRectificationScoopRecharge: (zones) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          rectification: {
            ...state.appliedTools.rectification,
            scoopRecharge: {
              applied: true,
              zones: zones.map(zone => ({
                id: zone.id,
                name: zone.name,
                summitValue: zone.summitValue,
                color: zone.color,
                points: zone.points ? [...zone.points] : [],
                bezierHandles: zone.bezierHandles ? [...zone.bezierHandles] : []
                // Note: Not saving influences as they are large Float32Arrays and not needed for display
              })),
              appliedAt: new Date().toISOString()
            }
          }
        }
      }));
    },

    saveRectificationSmoothening: (zones) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          rectification: {
            ...state.appliedTools.rectification,
            smoothening: {
              applied: true,
              zones: zones.map(zone => ({
                id: zone.id,
                name: zone.name,
                smootheningValue: zone.smootheningValue,
                color: zone.color,
                isExpanded: zone.isExpanded
                // Note: Not saving influences as they are large Float32Arrays and not needed for display
              })),
              appliedAt: new Date().toISOString()
            }
          }
        }
      }));
    },

    saveFlare: (flareConfig) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          flares: {
            ...state.appliedTools.flares,
            flare: {
              applied: true,
              flareValues: flareConfig.flareValues,
              segmentHeightValues: flareConfig.segmentHeightValues,
              segmentDeflectValues: flareConfig.segmentDeflectValues,
              segmentFalloffHeightValues: flareConfig.segmentFalloffHeightValues,
              segmentFalloffDeflectValues: flareConfig.segmentFalloffDeflectValues,
              deflectBlendRatio: flareConfig.deflectBlendRatio,
              appliedAt: new Date().toISOString(),
            },
          },
        },
      }));
    },

    saveFlaresSmoothening: (zones) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          flares: {
            ...state.appliedTools.flares,
            smoothening: {
              applied: true,
              zones: zones.map((zone) => ({
                id: zone.id,
                name: zone.name,
                smootheningValue: zone.smootheningValue,
                color: zone.color,
                isExpanded: zone.isExpanded,
              })),
              appliedAt: new Date().toISOString(),
            },
          },
        },
      }));
    },

    saveElongation: (value, unit) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          elongation: {
            enabled: true,
            applied: true,
            value,
            unit,
            appliedAt: new Date().toISOString()
          }
        }
      }));
    },

    saveThickness: (value, topValue, unit) => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          thickness: {
            enabled: true,
            applied: true,
            value,
            topValue,
            unit,
            appliedAt: new Date().toISOString()
          }
        }
      }));
    },

    saveTrimline: () => {
      set(state => ({
        appliedTools: {
          ...state.appliedTools,
          trimline: {
            applied: true,
            appliedAt: new Date().toISOString()
          }
        }
      }));
    },

    // Utility functions to get applied tools summary
    getAppliedToolsSummary: () => {
      const { appliedTools } = get();
      const summary = [];

      // Rectification tools
      if (appliedTools.rectification.measureReduction.applied) {
        summary.push({
          category: 'Rectification',
          tool: 'Measure Reduction',
          data: appliedTools.rectification.measureReduction,
          appliedAt: appliedTools.rectification.measureReduction.appliedAt
        });
      }

      if (appliedTools.rectification.globalReduction.applied) {
        summary.push({
          category: 'Rectification',
          tool: 'Global Reduction',
          data: appliedTools.rectification.globalReduction,
          appliedAt: appliedTools.rectification.globalReduction.appliedAt
        });
      }

      if (appliedTools.rectification.scoopRecharge.applied) {
        summary.push({
          category: 'Rectification',
          tool: 'Scoop/Recharge',
          data: appliedTools.rectification.scoopRecharge,
          appliedAt: appliedTools.rectification.scoopRecharge.appliedAt
        });
      }

      if (appliedTools.rectification.smoothening.applied) {
        summary.push({
          category: 'Rectification',
          tool: 'Smoothening',
          data: appliedTools.rectification.smoothening,
          appliedAt: appliedTools.rectification.smoothening.appliedAt
        });
      }

      if (appliedTools.flares.flare.applied) {
        summary.push({
          category: 'Flares',
          tool: 'Flare',
          data: appliedTools.flares.flare,
          appliedAt: appliedTools.flares.flare.appliedAt,
        });
      }

      if (appliedTools.flares.smoothening.applied) {
        summary.push({
          category: 'Flares',
          tool: 'Smoothening',
          data: appliedTools.flares.smoothening,
          appliedAt: appliedTools.flares.smoothening.appliedAt,
        });
      }

      // Elongation
      if (appliedTools.elongation.applied) {
        summary.push({
          category: 'Elongation',
          tool: 'Elongation',
          data: appliedTools.elongation,
          appliedAt: appliedTools.elongation.appliedAt
        });
      }

      // Thickness
      if (appliedTools.thickness.applied) {
        summary.push({
          category: 'Thickness',
          tool: 'Thickness',
          data: appliedTools.thickness,
          appliedAt: appliedTools.thickness.appliedAt
        });
      }

      if (appliedTools.trimline.applied) {
        summary.push({
          category: 'Trimline',
          tool: 'Trimline',
          data: appliedTools.trimline,
          appliedAt: appliedTools.trimline.appliedAt
        });
      }

      return summary.sort((a, b) => new Date(a.appliedAt) - new Date(b.appliedAt));
    },

    // Reset all finalization data
    reset: () => set({
      appliedTools: {
        rectification: {
          measureReduction: { applied: false, measurements: [], appliedAt: null },
          globalReduction: { applied: false, value: 0, valueType: '%', unit: 'mm', appliedAt: null },
          scoopRecharge: { applied: false, zones: [], appliedAt: null },
          smoothening: { applied: false, zones: [], appliedAt: null }
        },
        flares: {
          flare: {
            applied: false,
            flareValues: [],
            segmentHeightValues: [],
            segmentDeflectValues: [],
            segmentFalloffHeightValues: [],
            segmentFalloffDeflectValues: [],
            deflectBlendRatio: 0.75,
            appliedAt: null
          },
          smoothening: { applied: false, zones: [], appliedAt: null },
        },
        elongation: { applied: false, value: 0.0, unit: 'mm', appliedAt: null },
        thickness: { applied: false, value: 0.0, topValue: 0.0, unit: 'mm', appliedAt: null },
        trimline: { applied: false, appliedAt: null }
      }
    })
  }), {
    name: 'finalization-data-store'
  })
);

export default useFinalizationDataStore;